<template>
  <Teleport to="body">
    <div
      class="overlay l11-waap"
      data-qa="modal-overlay"
      :class="[size == 'full' ? 'overlaybg' : '']"
      v-if="show"
      @click="closeModal()"
    >
      <focus-trap :active="show">
        <div :class="['modal-wrapper', size]" tabindex="-1" data-qa="modal" @click.stop>
          <div class="modal-content-wrapper" ref="modalContainerRef">
            <div class="modal-content">
              <slot name="header">
                <div class="modal-header" data-qa="modal-header" v-if="!hideHeader">
                  <div class="modal-title-holder">
                    <div
                      class="modal-title-icon"
                      data-qa="modal-title-icon"
                      v-if="titleIconComponent"
                    >
                      <component :is="titleIconComponent" v-bind="titleIconProps" class="icon" />
                    </div>
                    <div class="modal-titles">
                      <div class="modal-title" data-qa="modal-title">
                        {{ title }}
                      </div>
                      <div class="modal-subtitle" data-qa="modal-subtitle">
                        {{ subtitle }}
                      </div>
                    </div>
                  </div>
                  <rbz-form-button
                    variant="tertiary"
                    v-if="!hideCloseIcon"
                    icon="IconClose"
                    :icon-props="{ color: 'dusty-gray' }"
                    @button-click="closeModal()"
                    :ignore-update-in-process="true"
                    class="close-modal-button"
                    data-qa="close-modal-button"
                  />
                </div>
              </slot>
              <div class="modal-body" data-qa="modal-body" ref="modalBodyRef">
                <slot name="content" :container="modalContainerRef" />
              </div>
            </div>
            <div class="modal-footer" data-qa="modal-footer" v-if="!hideFooter">
              <rbz-form-button
                v-if="tertiaryBtnTitle"
                variant="tertiary"
                data-qa="tertiary-modal-button"
                class="tertiary-modal-button"
                :label="tertiaryBtnTitle"
                :is-loading="tertiaryBtnIsLoading"
                @button-click="$emit('onTertiaryBtnClick')"
              />
              <div class="default-buttons-holder">
                <rbz-form-button
                  v-if="secondaryBtnTitle"
                  variant="secondary"
                  data-qa="secondary-modal-button"
                  class="secondary-modal-button"
                  :label="secondaryBtnTitle"
                  @button-click="$emit('onSecondaryBtnClick')"
                />
                <rbz-form-button
                  v-if="primaryBtnTitle"
                  variant="primary"
                  data-qa="primary-modal-button"
                  class="primary-modal-button"
                  :disabled="primaryBtnDisabled"
                  :label="primaryBtnTitle"
                  :ignore-update-in-process="primaryBtnIgnoreUpdateInProcess"
                  @button-click="$emit('onPrimaryBtnClick')"
                />
              </div>
            </div>
          </div>
        </div>
      </focus-trap>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import RbzFormButton from './RbzFormButton.vue';
import { computed, ref, watch } from 'vue';
import { GenericObject } from '@/types';
import { getIconComponent } from 'ui-component-library/assets/icons';

type ModalSize = 'full' | 'large' | 'medium' | 'small' | 'wizard';

interface BasicModal {
  show: boolean;
  title?: string;
  subtitle?: string;
  size?: ModalSize;
  hideCloseIcon?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  primaryBtnTitle?: string;
  primaryBtnDisabled?: boolean;
  primaryBtnIgnoreUpdateInProcess?: boolean;
  secondaryBtnTitle?: string;
  tertiaryBtnTitle?: string;
  tertiaryBtnIsLoading?: boolean;
  titleIcon?: string;
  titleIconProps?: GenericObject;
}

const props = withDefaults(defineProps<BasicModal>(), {
  size: 'medium',
  primaryBtnTitle: '',
  secondaryBtnTitle: '',
  tertiaryBtnTitle: '',
});

interface rbzModalDialogEmits {
  (e: 'closeModal'): void;

  (e: 'onTertiaryBtnClick'): void;

  (e: 'onSecondaryBtnClick'): void;

  (e: 'onPrimaryBtnClick'): void;

  (e: 'modalBodyRef', value: HTMLElement): void;
}

const emit = defineEmits<rbzModalDialogEmits>();
const modalBodyRef = ref(null);
const modalContainerRef = ref(null);
watch(
  modalBodyRef,
  () => {
    emit('modalBodyRef', modalBodyRef.value);
  },
  {
    immediate: true,
  }
);

const titleIconComponent = computed(() => {
  return props.titleIcon ? getIconComponent(props.titleIcon) : '';
});

watch(
  () => props.show,
  (value) => {
    if (value) {
      document.addEventListener('keydown', closeModalListener);
    } else {
      document.removeEventListener('keydown', closeModalListener);
    }
  }
);

const closeModal = () => {
  emit('closeModal');
  document.removeEventListener('keydown', closeModalListener);
};

const closeModalListener = (event: Event) => {
  if ((event as KeyboardEvent).code === 'Escape') {
    closeModal();
    event.stopPropagation();
  }
};
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.overlay {
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  inset: 0;
  display: flex;
  justify-content: center;
  position: fixed;
  z-index: 99990;

  &.overlaybg {
    background: map.get(colors.$colors, 'stratos');
  }

  .modal-wrapper {
    align-items: flex-start;
    background: map.get(colors.$colors, 'white');
    border-radius: 2px;
    box-shadow:
      0 3px 5px rgba(9, 30, 66, 0.2),
      0 0 1px rgba(9, 30, 66, 0.31);
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-height: 75vh;
    outline: 0;
    width: 100%;
  }

  .modal-content-wrapper {
    max-width: 100%;
    width: 100%;
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .modal-content {
    align-items: flex-start;
    background-color: inherit;
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 100%;
    width: 100%;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .modal-footer {
    display: flex;
    padding: 0 24px;
    width: 100%;

    .default-buttons-holder {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      width: 100%;
    }
  }

  .modal-wrapper > *:first-child {
    padding-top: 24px;
  }

  .modal-wrapper > *:last-child {
    padding-bottom: 24px;
  }

  .full {
    width: 95%;
    height: 95%;
    max-height: none;
    max-width: none;
    border-radius: 5px;

    .close-modal-button {
      position: absolute;
      top: 50px;
      right: 45px;
    }

    .modal-content {
      border-radius: 5px;
    }

    .modal-body {
      padding: 0 10px;
    }
  }

  .large {
    max-width: 624px;
  }

  .medium {
    max-width: 600px;
  }

  .small {
    max-width: 480px;
  }

  .wizard {
    max-width: 1008px;
    background: none;
    box-shadow: none;
    min-height: 845px;

    .modal-content {
      padding-top: 0;
      padding-bottom: 0;
    }

    .modal-header {
      margin-bottom: -68px;
      padding: 45px 75px 8px;
    }
  }

  @media (height <= 865px) {
    .wizard {
      height: calc(100vh - 80px);
      min-height: 0;
      max-height: none;
    }

    .wizard .modal-header {
      margin-bottom: -48px;
      padding: 0 0 8px;
    }
  }
}

.modal-header {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 0 24px 8px;
  width: 100%;

  .modal-title-holder {
    align-items: center;
    display: flex;
    flex-grow: 1;
  }

  .modal-title-icon {
    display: flex;
    margin-right: 10px;
    max-width: 50px;
  }

  .modal-title {
    color: map.get(colors.$colors, 'black');
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }

  .modal-subtitle {
    color: map.get(colors.$colors, 'shuttle-gray');
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  :deep(.close-modal-button .rbz-button) {
    border: 0;
    height: 24px;
    padding: 0;
    width: 24px;
  }
}

.modal-body {
  background-color: inherit;
  color: map.get(colors.$colors, 'black');
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  overflow-y: auto;
  padding: 0 24px;
  width: 100%;
  flex: 1;
  min-height: 0;
}
</style>
